import React, { useState, useEffect } from 'react';
import API from '../api';
import UserAvatar from './UserAvatar';

function logoUrl(abbr) {
  return 'https://a.espncdn.com/i/teamlogos/nfl/500/' + abbr.toLowerCase() + '.png';
}

function TeamLogo({ abbr, size }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img src={logoUrl(abbr)} alt={abbr} width={size} height={size}
      style={{ objectFit:'contain', display:'block' }} onError={() => setOk(false)} />
  ) : (
    <div style={{ width:size, height:size, background:'#334', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.3, color:'#aab', fontWeight:700 }}>{abbr}</div>
  );
}

export default function PickReveal({ poolId, selectedWeekId, user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = '/picks/reveal?pool_id=' + poolId + (selectedWeekId ? '&week_id=' + selectedWeekId : '');
    API.get(url).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [poolId, selectedWeekId]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#667' }}>Loading picks...</div>
    </div>
  );
  if (!data || !data.games) return null;

  const { members, games, picks, week } = data;
  const regularGames = games.filter(g => !g.is_tiebreaker);

  // Per-game lock check
  function isGameLocked(game) {
    if (!game.game_date_iso) return game.status !== 'scheduled';
    return Date.now() >= new Date(game.game_date_iso).getTime() - 5 * 60 * 1000;
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:2000, overflow:'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ maxWidth:1100, margin:'0 auto', padding:'24px 16px' }}>

        {/* Header. It used to scroll away with the grid, so on a phone the only
            way out was to guess: the panel fills the width, which leaves no
            backdrop to tap, and sixteen games of table sit between you and the
            button. It stays put now, and there is a second way out at the
            bottom for anyone who reads to the end. */}
        <div style={{ position:'sticky', top:0, zIndex:5, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:20, marginLeft:-16, marginRight:-16, padding:'14px 16px', background:'rgba(8,10,14,0.97)', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:22, fontWeight:700, color:'#fff', marginBottom:2 }}>
              PICK REVEAL
            </div>
            <div style={{ fontSize:13, color:'#556', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{week?.label} · {members.length} players</div>
          </div>
          <button onClick={onClose} aria-label="Close pick reveal"
            style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, minHeight:44, padding:'0 18px', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:10, color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>
            <span style={{ fontSize:18, lineHeight:1 }}>✕</span> Close
          </button>
        </div>

        {/* Grid */}
        <div style={{ overflowX:'auto' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', minWidth: 200 + members.length * 90 }}>
            {/* Member header */}
            <thead>
              <tr>
                <th style={{ width:160, textAlign:'left', padding:'8px 12px', fontSize:11, color:'#445', fontWeight:700, letterSpacing:1, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>GAME</th>
                {members.map(m => (
                  <th key={m.id} style={{ width:90, textAlign:'center', padding:'8px 6px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <UserAvatar username={m.username} size={28} />
                      <span style={{ fontSize:10, color: m.id === user?.id ? '#7fb3ff' : '#667', fontWeight:700, maxWidth:80, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {m.username}{m.id === user?.id ? ' ★' : ''}
                      </span>
                    </div>
                  </th>
                ))}
                <th style={{ width:100, textAlign:'center', padding:'8px 6px', fontSize:11, color:'#445', fontWeight:700, letterSpacing:1, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>SPLIT</th>
              </tr>
            </thead>
            <tbody>
              {regularGames.map(function(game) {
                const locked = isGameLocked(game);
                const isFinal = game.status === 'final';

                // Tally picks
                let homePicks = 0, awayPicks = 0, noPicks = 0;
                members.forEach(m => {
                  const p = picks[m.id] && picks[m.id][game.id];
                  if (!p) noPicks++;
                  else if (p.picked_team === game.home_abbr) homePicks++;
                  else awayPicks++;
                });

                return (
                  <tr key={game.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    {/* Game info */}
                    <td style={{ padding:'10px 12px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <TeamLogo abbr={game.away_abbr} size={22} />
                        <span style={{ fontSize:11, color:'#667', fontWeight:700 }}>@</span>
                        <TeamLogo abbr={game.home_abbr} size={22} />
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:'#ccd' }}>{game.away_abbr} @ {game.home_abbr}</div>
                          {isFinal && (
                            <div style={{ fontSize:10, color:'#4caf50', fontWeight:700 }}>
                              FINAL {game.away_score}-{game.home_score}
                            </div>
                          )}
                          {!isFinal && !locked && (
                            <div style={{ fontSize:10, color:'#556' }}>
                              {game.game_date ? game.game_date + ' · ' : ''}{game.game_time}
                            </div>
                          )}
                          {!isFinal && locked && (
                            <div style={{ fontSize:10, color:'#ffa000' }}>LOCKED</div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Each member's pick */}
                    {members.map(function(m) {
                      const p = picks[m.id] && picks[m.id][game.id];
                      const isMe = (m.user_id ?? m.id) === user?.id;

                      if (!locked && !isFinal) {
                        // Hidden until locked
                        return (
                          <td key={m.id} style={{ textAlign:'center', padding:'10px 6px', background: isMe ? 'rgba(1,51,105,0.1)' : 'transparent' }}>
                            <span style={{ fontSize:18, opacity:0.3 }}>?</span>
                          </td>
                        );
                      }

                      if (!p) {
                        return (
                          <td key={m.id} style={{ textAlign:'center', padding:'10px 6px', background: isMe ? 'rgba(1,51,105,0.1)' : 'transparent' }}>
                            <span style={{ fontSize:10, color:'#D50A0A', fontWeight:700 }}>MISSED</span>
                          </td>
                        );
                      }

                      const pickedHome = p.picked_team === game.home_abbr;
                      const isCorrect = isFinal ? (p.is_correct === 1) : null;
                      // A tie leaves is_correct null on every pick in the
                      // column, so a plain truthiness test marks the whole row
                      // wrong. Nobody was wrong — the game was a push.
                      const isPush = isFinal && p.is_correct === null;
                      const confColor = { low:'#556', medium:'#ffa000', high:'#4caf50' };

                      return (
                        <td key={m.id} style={{ textAlign:'center', padding:'10px 6px', background: isMe ? 'rgba(1,51,105,0.1)' : 'transparent' }}>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                            <TeamLogo abbr={p.picked_team} size={26} />
                            <span style={{ fontSize:10, fontWeight:700, color:'#aab' }}>{p.picked_team}</span>
                            {isFinal && (
                              <span style={{ fontSize:14 }}>{isPush ? '🤝' : isCorrect ? '✅' : '❌'}</span>
                            )}
                            {p.confidence && (
                              <span style={{ fontSize:9, color: confColor[p.confidence] || '#445', fontWeight:700, letterSpacing:0.5 }}>
                                {p.confidence.toUpperCase()}
                              </span>
                            )}
                            {p.pick_comment && (
                              <span title={p.pick_comment} style={{ fontSize:9, color:'#445', maxWidth:70, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                "{p.pick_comment}"
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}

                    {/* Split column */}
                    <td style={{ textAlign:'center', padding:'10px 8px' }}>
                      {locked || isFinal ? (
                        <div style={{ fontSize:11 }}>
                          <div style={{ color:'#7fb3ff', fontWeight:700 }}>{awayPicks} {game.away_abbr}</div>
                          <div style={{ color:'#445', fontSize:9 }}>vs</div>
                          <div style={{ color:'#7fb3ff', fontWeight:700 }}>{homePicks} {game.home_abbr}</div>
                          {noPicks > 0 && <div style={{ color:'#D50A0A', fontSize:9 }}>{noPicks} missed</div>}
                        </div>
                      ) : (
                        <span style={{ fontSize:10, color:'#334' }}>hidden</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* The way out for anyone who scrolled the whole grid. */}
        <button onClick={onClose}
          style={{ display:'block', width:'100%', marginTop:20, minHeight:48, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:10, color:'#dde', cursor:'pointer', fontSize:15, fontWeight:700 }}>
          Close
        </button>
        {/* Clearance so the bottom Close is not thumb-adjacent to the tab bar
            showing through underneath it. */}
        <div style={{ height:88 }} />
      </div>
    </div>
  );
}
